import SidebarShell from "@/components/sidebar-shell";
import styles from "@/components/upload-center.module.css";
import { fileExists, openFile, readTextFile, save } from "@/file";
import globals from "@/globals";
import { currentFolder, files, pendingUploadFiles } from "@/signals";
import { importSite } from "@/site";
import { FileUploadStatus, SiteContent } from "@/types";
import { closeUploadCenter, getPremium, parseJson, showDialog } from "@/utils";
import {
	faCheck,
	faPause,
	faSpinner,
	faTimes,
	faTriangleExclamation,
	faUpload,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { t } from "i18next";
import { JSX, useCallback, useMemo, useState } from "react";

const MB = 1048576;

function UploadCenter(): JSX.Element {
	const [allFilesStatus, setAllFilesStatus] = useState<FileUploadStatus>(
		FileUploadStatus.NOT_UPLOADED,
	);
	const [fixedCurrentFolder, setFixedCurrentFolder] = useState<string | null>(
		null,
	);
	const [showUploadButton, setShowUploadButton] = useState<boolean>(true);

	const tip = useMemo(() => {
		for (const fileInfo of pendingUploadFiles.value) {
			if (fileInfo.path.toLowerCase().includes("jquery")) {
				return t("noNeedToUploadJquery");
			}
			if (fileInfo.type.startsWith("text/")) {
				return t("recommendWritingCodeOnWebPage");
			}
		}
		return "";
	}, []);

	const confirmMultiplePhpFiles = useCallback(
		(phpCount: number): Promise<void> => {
			return showDialog(
				t("multiplePhpFilesToBeUploaded", {
					count: phpCount,
				}) + t("confirmContinueUpload"),
				{
					countdown: 5,
					showCancel: true,
				},
			);
		},
		[],
	);

	const uploadFile = useCallback(async (asBinary: boolean): Promise<void> => {
		const filesInfo = pendingUploadFiles.value;
		setAllFilesStatus(FileUploadStatus.UPLOADING);
		setFixedCurrentFolder(currentFolder.value);
		setShowUploadButton(false);

		for (const fileInfo of filesInfo) {
			const file = globals.pendingUploadFiles[fileInfo.path];
			if (!file) {
				fileInfo.skipReason = t("fileNotFound");
				fileInfo.status = FileUploadStatus.FAILED;
				continue;
			}

			if (file.size > 5 * MB) {
				void showDialog(
					t("fileCannotLargerThanOtherwiseAffectSpeed", {
						size: "5 MB",
					}) +
						((): string => {
							if (file.type.startsWith("image/")) {
								return t("largeImageSolution");
							} else if (file.type.startsWith("video/")) {
								return t("largeVideoSolution");
							} else {
								return t("largeFileSolution");
							}
						})(),
				);
				fileInfo.skipReason = t("fileTooLarge");
				fileInfo.status = FileUploadStatus.FAILED;
				setAllFilesStatus(FileUploadStatus.FAILED);
				return;
			}

			const path = currentFolder.value + fileInfo.path;
			const pathLowered = path.toLowerCase();
			if (pathLowered === "settings.rth") {
				fileInfo.skipReason = t("thisIsInternalFile");
				fileInfo.status = FileUploadStatus.FAILED;
				continue;
			}

			if (pathLowered.endsWith(".scss")) {
				fileInfo.skipReason = t("fileNotNeededForRunningSite");
				fileInfo.status = FileUploadStatus.FAILED;
				continue;
			}

			const isAccepted = globals.accept.test(fileInfo.path);
			const isBinary =
				(asBinary &&
					fileInfo.path !== files.value["settings.rth"]?.home) ||
				(!isAccepted &&
					!file.type.includes("text/") &&
					!pathLowered.endsWith(".php")) ||
				(globals.privilege && pathLowered.includes(".min."));

			if (!globals.privilege && (!isAccepted || isBinary)) {
				fileInfo.skipReason = t("premiumRequired");
				fileInfo.status = FileUploadStatus.FAILED;
				setAllFilesStatus(FileUploadStatus.FAILED);
				await showDialog(
					t("premiumRequiredForUploadingThisFileType") +
						t("refreshIfJustPaid"),
					{
						showCancel: true,
					},
				);
				void getPremium();
				return;
			}

			if (isBinary) {
				if (fileExists(path)) {
					fileInfo.skipReason = t("fileAlreadyExists");
					fileInfo.status = FileUploadStatus.FAILED;
					continue;
				}
				fileInfo.status = FileUploadStatus.UPLOADING;
				const hasSaved = await save({
					content: file,
					hideLoading: true,
					key: path,
				});
				fileInfo.status = hasSaved
					? FileUploadStatus.UPLOADED
					: FileUploadStatus.FAILED;
			} else {
				const content = await readTextFile(file);
				if (file.size <= 2 * MB && pathLowered.endsWith(".json")) {
					const json = parseJson<SiteContent>(content);
					if (json["settings.rth"]) {
						if (filesInfo.length === 1) {
							void importSite(json);
						} else {
							fileInfo.skipReason = t("thisIsInternalFile");
							fileInfo.status = FileUploadStatus.FAILED;
							continue;
						}
						return;
					}
				}

				if (files.value[path] === content) {
					fileInfo.skipReason = t("fileContentSame");
					fileInfo.status = FileUploadStatus.FAILED;
					continue;
				}

				files.value = {
					...files.value,
					[path]: content,
				};
				fileInfo.status = FileUploadStatus.UPLOADING;
				const hasSaved = await save({
					hideLoading: true,
					key: path,
				});
				fileInfo.status = hasSaved
					? FileUploadStatus.UPLOADED
					: FileUploadStatus.FAILED;
			}
		}

		setAllFilesStatus(FileUploadStatus.UPLOADED);
		const fileToOpen = filesInfo.find((fileInfo) => {
			return (
				fileInfo.status === FileUploadStatus.UPLOADED &&
				fileInfo.type.startsWith("text/")
			);
		});
		if (fileToOpen) {
			void openFile(fileToOpen.path);
		}
	}, []);

	const handleUploadClick = useCallback(async (): Promise<void> => {
		const filesCount = pendingUploadFiles.value.length;
		if (!globals.privilege && filesCount > 1) {
			await showDialog(
				t("premiumRequiredForUploadingMultipleFiles") +
					t("refreshIfJustPaid"),
				{
					showCancel: true,
				},
			);
			void getPremium();
			return;
		}

		let phpCount = 0;
		let size = 0;
		for (const key in globals.pendingUploadFiles) {
			const file = globals.pendingUploadFiles[key];
			if (file.name.includes("wp-")) {
				void showDialog(t("wordpressUnsupported"));
				return;
			}
			if (file.name.endsWith(".php")) {
				phpCount++;
			}
			if (globals.accept.test(file.name) || file.type.includes("text/")) {
				size += file.size;
			}
		}

		if (phpCount > 1) {
			await confirmMultiplePhpFiles(phpCount);
		}

		if (globals.privilege && size > 2 * MB) {
			if (filesCount <= 1) {
				void showDialog(t("noEnoughSpace"));
			} else {
				await showDialog(
					t("textFilesToBeUploadedTooLarge") +
						t("confirmContinueUpload"),
					{
						showCancel: true,
					},
				);
				void uploadFile(true);
			}
		} else {
			void uploadFile(false);
		}
	}, [confirmMultiplePhpFiles, uploadFile]);

	const listElements = pendingUploadFiles.value.map((fileInfo) => (
		<li
			key={fileInfo.path}
			className={styles["list-item"]}
		>
			<div className="with-icon">
				{fileInfo.status === FileUploadStatus.NOT_UPLOADED && (
					<FontAwesomeIcon
						className="icon"
						fixedWidth
						icon={faPause}
					/>
				)}
				{fileInfo.status === FileUploadStatus.UPLOADING && (
					<FontAwesomeIcon
						className="icon"
						fixedWidth
						icon={faSpinner}
						spinPulse
					/>
				)}
				{fileInfo.status === FileUploadStatus.UPLOADED && (
					<FontAwesomeIcon
						className="icon"
						fixedWidth
						icon={faCheck}
					/>
				)}
				{fileInfo.status === FileUploadStatus.FAILED && (
					<FontAwesomeIcon
						className="icon"
						icon={faTimes}
						fixedWidth
					/>
				)}
				<div className="text">
					{(fixedCurrentFolder ?? currentFolder.value) +
						fileInfo.path}
					{fileInfo.skipReason && (
						<div className={styles["description"]}>
							{fileInfo.skipReason}
						</div>
					)}
				</div>
			</div>
		</li>
	));

	return (
		<SidebarShell
			onClose={closeUploadCenter}
			title={
				<>
					<FontAwesomeIcon
						className="icon"
						fixedWidth
						icon={faUpload}
					/>
					<span className="text">{t("uploadCenter")}</span>
				</>
			}
		>
			<>
				{allFilesStatus === FileUploadStatus.UPLOADING && (
					<div className="with-icon">
						<FontAwesomeIcon
							className="icon"
							fixedWidth
							icon={faSpinner}
							spinPulse
						/>
						<span className="text">{t("uploading")}</span>
					</div>
				)}
				{allFilesStatus === FileUploadStatus.UPLOADED && (
					<div className="with-icon">
						<FontAwesomeIcon
							className="icon"
							fixedWidth
							icon={faCheck}
						/>
						<span className="text">{t("allFilesUploaded")}</span>
					</div>
				)}
				{tip && (
					<div className="with-icon">
						<FontAwesomeIcon
							className="icon"
							fixedWidth
							icon={faTriangleExclamation}
						/>
						<span className="text">{tip}</span>
					</div>
				)}
				{showUploadButton && (
					<button
						className="button"
						type="submit"
						onClick={() => void handleUploadClick()}
					>
						{t("startUpload")}
					</button>
				)}
				<ul className={styles["list"]}>{listElements}</ul>
			</>
		</SidebarShell>
	);
}

export default UploadCenter;
